// 📄 pages/Home/HerramientasRail.tsx
//
// Accesos directos a las Herramientas GLOBALES (2026-08-20, extraído de
// HomeLanding.tsx el 2026-08-27 a pedido del usuario: la fila horizontal de
// chips "se perdía" visualmente y no escalaba a medida que se agregaban
// más herramientas — ver [[proyecto_guias_integracion]]). Mismos catálogos
// que ya viven en "Ariba Toolbox" (AribaHome.tsx), no datos nuevos ni una
// segunda fuente de verdad, solo un atajo desde el Home.
//
// Desktop (md+): riel vertical fijo (sticky) angosto con solo íconos, que
// se expande mostrando las etiquetas al pasar el mouse — más un botón de
// "pin" (📌) para dejarlo abierto sin necesidad de hover, imprescindible
// en touch (tablet/desktop híbrido) donde no existe hover real.
// Mobile (<md): vuelve a la fila horizontal de chips con scroll (el riel
// vertical angosto no tiene sentido en una pantalla angosta).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LucideIcon, Sparkles, BookOpenCheck, ShieldCheck, Database, Terminal,
  ArrowLeftRight, Table2, AlertTriangle, PanelLeftClose, PanelLeftOpen
} from 'lucide-react';

interface HerramientaGlobal {
  id: string;
  label: string;
  icon: LucideIcon;
  // La mayoría navega con el switch de páginas interno (setPage). "Buscar
  // con IA" en cambio vive en una ruta de React Router (/mi-espacio, ver
  // MiEspacioHome.tsx) — si viene `href`, se usa navigate() en vez de
  // setPage(page), mismo criterio que ya usan las HOME_CARDS de HomeLanding.tsx.
  page?: string;
  href?: string;
}

const HERRAMIENTAS_GLOBALES: HerramientaGlobal[] = [
  { id: 'busqueda-ia', label: 'Buscar con IA', icon: Sparkles, href: '/mi-espacio?tab=busqueda-ia' },
  { id: 'guias-integracion', label: 'Guías de Integración', icon: BookOpenCheck, page: 'guias-integracion-toolbox' },
  { id: 'validador', label: 'Validador de Estructura', icon: ShieldCheck, page: 'validador-estructura' },
  { id: 'badis', label: 'BAdIs y Objetos ABAP', icon: Database, page: 'badis-toolbox' },
  { id: 'transacciones', label: 'Transacciones SAP', icon: Terminal, page: 'transacciones-toolbox' },
  { id: 'interfaces', label: 'Interfaces / Canales', icon: ArrowLeftRight, page: 'interfaces-toolbox' },
  { id: 'tablas', label: 'Tablas SAP', icon: Table2, page: 'tablas-toolbox' },
  { id: 'errores', label: 'Errores Conocidos', icon: AlertTriangle, page: 'errores-conocidos-toolbox' }
];

interface HerramientasRailProps {
  requireAuth: (callback: () => void) => void;
  setPage: (page: string) => void;
}

export default function HerramientasRail({ requireAuth, setPage }: HerramientasRailProps) {
  const navigate = useNavigate();
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const abierto = hover || pinned;

  const ir = (item: HerramientaGlobal) => requireAuth(() => (item.href ? navigate(item.href) : setPage(item.page!)));

  return (
    <>
      {/* Desktop: menú lateral completo — fondo SÓLIDO (bg-gray-800, mismo
          token que las cards de "Accesos rápidos" y HOME_CARDS, remapeado
          por theme) y ALTURA COMPLETA del contenido de la derecha (2026-08-27,
          2do pedido del usuario: primero pidió sacar la transparencia
          "glass" porque se veía invisible, después pidió que dejara de
          "flotar en el aire" corto y ocupara todo el alto — acá se resuelve
          con `items-stretch` en el flex row del HomeLanding.tsx que lo
          contiene, más `h-full` en la tarjeta de acá adentro). Ya no
          necesita `sticky`: al tener SIEMPRE el mismo alto que el contenido
          principal (son hermanos en el mismo flex row), nunca hay una
          distancia de scroll en la que uno termine antes que el otro. */}
      <aside className="hidden md:block flex-shrink-0">
        <div
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          className={`relative h-full flex flex-col gap-1 p-3 rounded-2xl bg-gray-800 border border-white/10 shadow-lg shadow-black/10 transition-[width] duration-300 ease-out overflow-hidden ${
            abierto ? 'w-60' : 'w-[68px]'
          }`}
        >
          <div className="flex items-center gap-2 px-1.5 pb-2 mb-1 border-b border-white/10">
            <span className={`text-[10px] font-semibold tracking-[0.15em] text-blue-400 uppercase whitespace-nowrap transition-opacity duration-200 ${abierto ? 'opacity-100' : 'opacity-0'}`}>
              Herramientas
            </span>
            <button
              type="button"
              onClick={() => setPinned((p) => !p)}
              title={pinned ? 'Desanclar' : 'Fijar abierto'}
              className="ml-auto flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-blue-400 hover:bg-white/5 transition-all"
            >
              {pinned ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeftOpen className="w-4 h-4" />}
            </button>
          </div>

          {HERRAMIENTAS_GLOBALES.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => ir(item)}
                title={item.label}
                className="group/item flex items-center gap-3 px-2.5 py-2.5 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all duration-200"
              >
                <Icon className="w-[18px] h-[18px] flex-shrink-0 text-blue-400 group-hover/item:drop-shadow-[0_0_6px_rgba(96,165,250,0.7)] transition-all duration-200" />
                <span className={`whitespace-nowrap transition-all duration-200 overflow-hidden ${abierto ? 'opacity-100 max-w-[160px]' : 'opacity-0 max-w-0'}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </aside>

      {/* Mobile: fila horizontal de chips con scroll, mismo criterio de antes */}
      <div className="flex md:hidden flex-wrap gap-2 w-full">
        {HERRAMIENTAS_GLOBALES.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => ir(item)}
              className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-blue-400/40 rounded-full px-3.5 py-1.5 text-xs text-gray-300 hover:text-white transition-all"
            >
              <Icon className="w-3.5 h-3.5 text-blue-400" />
              {item.label}
            </button>
          );
        })}
      </div>
    </>
  );
}
